/**
 * Notification hooks — Layer 3 of the three-layer TanStack pattern.
 *
 * Provides:
 *   useNotificationCenter()  — fetch notifications + unread count (combined query)
 *   useMarkRead()            — mark specific notifications as read
 *   useMarkAllRead()         — mark all notifications as read
 *   useNotificationList()    — paginated full list (full page view)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationCenterQueryOptions, notificationListQueryOptions } from "../api/notificationQueryOptions";
import { notificationKeys } from "@/shared/lib/queryKeys";
import { notificationMarkAllRead, notificationMarkRead } from "../api/notificationApi";
import type {
  NotificationCenterResponse,
  NotificationListResponse,
} from "../api/notificationApi";

/**
 * Optimistically flip `isRead` across BOTH notification caches.
 *
 * Flow: the dropdown center uses notificationKeys.list() ({ notifications }),
 * while the full-page list uses notificationKeys.inbox() (paginated { results }).
 * A mutation must touch both or one view goes stale until a hard reload.
 *
 * @param ids  Specific notification ids to mark read, or null to mark all.
 * @returns    Snapshots of both caches for rollback on error.
 */
function applyReadOptimistic(
  queryClient: ReturnType<typeof useQueryClient>,
  ids: string[] | null
): { prevList: unknown; prevInbox: unknown } {
  const shouldMark = (id: string) => ids === null || ids.includes(id);

  const prevList = queryClient.getQueryData(notificationKeys.list());
  queryClient.setQueryData(notificationKeys.list(), (old) => {
    if (!old || typeof old !== "object") return old;
    const data = old as NotificationCenterResponse;
    return {
      ...data,
      notifications: (data.notifications ?? []).map((n) =>
        shouldMark(n.id) ? { ...n, isRead: true } : n
      ),
    };
  });

  const prevInbox = queryClient.getQueryData(notificationKeys.inbox());
  queryClient.setQueryData(notificationKeys.inbox(), (old) => {
    if (!old || typeof old !== "object") return old;
    const data = old as NotificationListResponse;
    return {
      ...data,
      results: (data.results ?? []).map((n) =>
        shouldMark(n.id) ? { ...n, isRead: true } : n
      ),
    };
  });

  return { prevList, prevInbox };
}

/**
 * Fetch notification list + unread count in a single combined query.
 * Real-time delivery via SSE is handled centrally by useNotificationStream
 * (shared/hooks/useNotificationStream.ts) — this hook manages polling only.
 */
export function useNotificationCenter(options?: {
  enabled?: boolean;
}) {
  const { enabled = true } = options ?? {};

  const query = useQuery({
    ...notificationCenterQueryOptions(),
    enabled,
  });

  // Derive unread state directly from the notifications list — deterministic,
  // no race conditions with the separate unread-check endpoint.
  const notifications = query.data?.notifications ?? [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const hasUnread = unreadCount > 0;

  return {
    data: { notifications, unreadCount, hasUnread },
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    enabled,
  };
}

/** Mark specific notifications as read. Optimistic update on isRead. */
export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationMarkRead,
    onMutate: async (ids: string[]) => applyReadOptimistic(queryClient, ids),
    onError: (_err, _ids, context) => {
      if (context?.prevList !== undefined) {
        queryClient.setQueryData(notificationKeys.list(), context.prevList);
      }
      if (context?.prevInbox !== undefined) {
        queryClient.setQueryData(notificationKeys.inbox(), context.prevInbox);
      }
    },
    onSettled: () => {
      // Invalidate every notification cache (list, inbox, hasUnread badge) so all views re-sync.
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Fetch paginated notification list (full page view).
 * Returns the full DRF paginated response { count, next, previous, results }.
 */
export function useNotificationList(params?: { pageSize?: number }) {
  return useQuery(notificationListQueryOptions(params));
}

/** Mark all notifications as read. */
export function useMarkAllRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationMarkAllRead,
    onMutate: async () => applyReadOptimistic(queryClient, null),
    onError: (_err, _vars, context) => {
      if (context?.prevList !== undefined) {
        queryClient.setQueryData(notificationKeys.list(), context.prevList);
      }
      if (context?.prevInbox !== undefined) {
        queryClient.setQueryData(notificationKeys.inbox(), context.prevInbox);
      }
    },
    onSettled: () => {
      // Invalidate every notification cache (list, inbox, hasUnread badge) so all views re-sync.
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
